import React from 'react';
import { Box, AppBar, Toolbar, Typography, Button, IconButton, Drawer, List, ListItem, ListItemIcon, ListItemText, Divider, useMediaQuery, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LanguageIcon from '@mui/icons-material/Language';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Logo from './Logo';
import LanguageSwitcher from './LanguageSwitcher';
import SimpleCurrencySelector from './SimpleCurrencySelector';
import ThemeSwitcher from './ThemeSwitcher';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import { useSelector, useDispatch } from 'react-redux';
import { logout as logoutAction } from '../slices/authSlice';

const Layout = ({ children, isPublic = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useLanguage();
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  
  // Usuario desde Redux
  const reduxUser = useSelector(state => state.auth.user);
  const dispatch = useDispatch();

  // 17 sep 2026 (Augusto: "el menu de la izquierda esta mal" -- se veía
  // "title" siete veces y "logout"): estas llamadas tenían los argumentos
  // invertidos. La firma real de t() es t('seccion.clave', textoDeReemplazo)
  // -- una sola clave CON PUNTO (ver contexts/LanguageContext.js). Al pasar
  // t('dashboard', 'title'), 'title' viajaba como texto de reemplazo, la
  // clave 'dashboard' no existía en la sección common, y t() devolvía
  // exactamente el reemplazo: la palabra "title".
  //
  // Se corrigen a la forma con punto y, además, cada una lleva un fallback
  // en español de verdad: varias de estas secciones (debts, reports,
  // subscription, voice) NO tienen traducción cargada, así que sin un
  // fallback decente volverían a mostrar basura.
  const menuItems = [
    { text: t('dashboard.title', 'Inicio'), path: '/dashboard' },
    { text: t('transactions.title', 'Transacciones'), path: '/transactions' },
    { text: t('budgets.title', 'Presupuestos'), path: '/budgets' },
    { text: t('debts.title', 'Deudas'), path: '/debts' },
    { text: t('reports.title', 'Informes'), path: '/reports' },
    { text: t('subscription.title', 'Suscripción'), path: '/subscription' },
    { text: t('voice.title', 'Voz'), path: '/voice' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  // Manejador de logout usando Redux
  const handleLogout = () => {
    dispatch(logoutAction());
    navigate('/');
  };

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          <Logo variant="text" size="medium" />
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            onClick={() => {
              navigate(item.path);
              handleDrawerToggle();
            }}
          >
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem button onClick={() => {
          // Primero hacemos logout (esto borra el token y el estado de autenticación)
          dispatch(logoutAction());
          // Luego navegamos al login
          navigate('/');
        }}>
          <ListItemText primary={t('common.logout', 'Cerrar sesión')} />
        </ListItem>
      </List>
    </div>
  );
  // Este dispatch ya está declarado arriba
  // La función handleLogout ya está definida arriba

  const handleMenuClick = () => {
    // Implement drawer/menu logic
  };

  // If this is a public page, use the public layout
  if (isPublic) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <PublicHeader />
        <Box component="main" sx={{ flexGrow: 1, pt: 8 }}>
          {/* The pt: 8 adds padding to account for the fixed header */}
          {children}
        </Box>
        <PublicFooter />
      </Box>
    );
  }

  // Otherwise use the app layout with sidebar for authenticated users
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar position="fixed" color="primary">
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}
          >
            <Logo variant="white" size="small" />
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LanguageSwitcher />
            <SimpleCurrencySelector />
            <ThemeSwitcher />
            {user || reduxUser ? (
              <Button 
                color="inherit" 
                onClick={handleLogout}
                startIcon={<LogoutIcon />}
              >
                {t('general.logout', 'Logout')}
              </Button>
            ) : (
              <Button color="inherit" onClick={() => navigate('/login')}>
                {t('general.login', 'Login')}
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: 240 }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3, width: { md: `calc(100% - 240px)` } }}
      >
        <Toolbar /> {/* This empty Toolbar is necessary for spacing */}
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
